import { getBaseUrl } from "../config/getCompany";

const companyUrl = getBaseUrl()
const baseUrl = companyUrl
const accountUrl = `${baseUrl}/account`;
const shipperUrl = `${baseUrl}/shipper`;
const carrierUrl = `${baseUrl}/carrier`;
const employeeUrl = `${baseUrl}/employee`;
const resourcesUrl = `${baseUrl}/resources`;

/**
 * Employee OAuth entry points. These are deliberately **BU-free** and built without
 * `getBaseUrl()`: the business unit is only known after the id_token is verified, so
 * the login page and OAuth callback cannot sit under a `$company` segment.
 *
 * Unlike the rest of this file these are safe to use in server-side redirects —
 * everything built from `getBaseUrl()` resolves to `/uae` on the server regardless
 * of the caller's region.
 */
const employeeOauthUrl = "/app/employee";

const employeeOauth = {
  login: `${employeeOauthUrl}/login`,
  callback: `${employeeOauthUrl}/callback`,
};

/** Post-login employee home for a resolved BU: `/{bu}/app/employee`. */
const employeeHome = (bu: string) => `/${bu}/app/employee`;

export const route = {
  webUrls: {
    whatsApp: "https://api.whatsapp.com/send?text=",
  },

  employeeOauth,
  employeeHome,
  login: `${baseUrl}/login`,
  chooseAccountType: `${accountUrl}/select-type`,
  createAccount: `${accountUrl}/sign-up`,
  welcome: "welcome",
  registrationOtp: `${accountUrl}/verify-otp`,
  forgotPassword: `${accountUrl}/recover/forgot-password`,
  updatePassword: `${accountUrl}/recover/create-new-pin`,

  shipper: {
    indent: `${shipperUrl}/indent`,
    createIndent: `${shipperUrl}/indent/create`,
    users: `${shipperUrl}/users`,
    createUser: `${shipperUrl}/users/create`,
    profile: `${shipperUrl}/profile`,
    logout: `${shipperUrl}/logout`,
  },

  carrier: {
    users: `${carrierUrl}/account/users`,
    createUser: `${carrierUrl}/account/users/create`,
    indent: `${carrierUrl}/indent`,
    trips: `${carrierUrl}/trips`,
    active: `${carrierUrl}/trips/active`,
    delivered: `${carrierUrl}/trips/delivered`,
    balance: `${carrierUrl}/trips/balance`,
    paid: `${carrierUrl}/trips/paid`,
    truck: `${carrierUrl}/truck`,
    addTruck: `${carrierUrl}/truck/create`,
    account: `${carrierUrl}/account`,
    profile: `${carrierUrl}/account/profile`,
    tutorials: `${carrierUrl}/tutorials`,
    logout: `${carrierUrl}/account/logout`,
    help: `${carrierUrl}/account/help`,
    call: `${carrierUrl}/indent/livekit-receiver`,
    livekitCall: `${carrierUrl}/indent/livekit-call`,
  },

  employee: {
    login: `${employeeUrl}/login`,
    indent: `${employeeUrl}/indent`,
    livekitCall: `${employeeUrl}/indent/livekit-call`,
    createIndent: `${employeeUrl}/indent/create`,
    carrier: `${employeeUrl}/carrier`,
    createCarrier: `${employeeUrl}/carrier/create`,
    leadCarriers: `${employeeUrl}/carrier/lead`,
    createCarrierUser: `${employeeUrl}/carrier/user/create`,
    shipper: `${employeeUrl}/shipper`,
    createShipper: `${employeeUrl}/shipper/create`,
    leadShippers: `${employeeUrl}/shipper/lead`,
    createShipperUser: `${employeeUrl}/shipper/user/create`,
    users: `${employeeUrl}/users`,
    addEmployee: `${employeeUrl}/users/create`,
    addTeam: `${employeeUrl}/users/team`,
    carrierCityPreference: "/city-preference",
    carrierTruckTypePreference: "/truck-type-preference",
    trips: `${employeeUrl}/trips`,
    confirmed: `${employeeUrl}/trips/confirmed`,
    loading: `${employeeUrl}/trips/loading`,
    intransit: `${employeeUrl}/trips/intransit`,
    unloading: `${employeeUrl}/trips/unloading`,
    delivered: `${employeeUrl}/trips/delivered`,
    invoiced: `${employeeUrl}/trips/invoiced`,
    closed: `${employeeUrl}/trips/closed`,
    payments: `${employeeUrl}/payments`,
    shipperAdvance: `${employeeUrl}/payments/shipper-advance`,
    addcustomerOffice: `${employeeUrl}/trips/customer-office`,
    shipperBalance: `${employeeUrl}/payments/shipper-balance`,
    eligibleAdvance: `${employeeUrl}/payments/eligible-advance`,
    carrierAdvance: `${employeeUrl}/payments/carrier-advance`,
    carrierBalance: `${employeeUrl}/payments/carrier-balance`,
    creditDebit: `${employeeUrl}/payments/credit-debit`,
    bankPending: `${employeeUrl}/payments/pending-transactions`,
    statement: `${employeeUrl}/payments/statement`,
    report: `${employeeUrl}/report`,
    profile: `${employeeUrl}/profile`,
    logout: `${employeeUrl}/profile/logout`,
    trucks: `${employeeUrl}/trucks`,
    trucksCreate: `${employeeUrl}/trucks/create`,
    branch: `${employeeUrl}/shipper/branch`,
    requestSwift: `${employeeUrl}/request`,
    requestCity: `${employeeUrl}/request/city`,
    requestTruckType: `${employeeUrl}/request/trucktype`,
    createtSwift: `${employeeUrl}/request/createswift`,
    createtCity: `${employeeUrl}/request/createcity`,
    createTruckType: `${employeeUrl}/request/createtrucktype`,
    employeeDetail: `${employeeUrl}/employee/employee_id`,
    createSwiftCode: `${employeeUrl}/request/createswift`
  },

  resources: {
    sessionStatus: `${resourcesUrl}/sessionStatus`,
    shippers: `${resourcesUrl}/shippers`,
    shipperContact: `${resourcesUrl}/shipper-contact`,
    shipperStatement: `${resourcesUrl}/shipperStatement`,
    employees: `${resourcesUrl}/employees`,
    employeeFunctions: `${resourcesUrl}/employeeFunctions`,
    carriers: `${resourcesUrl}/carriers`,
    trucks: `${resourcesUrl}/trucks`,
    trips: `${resourcesUrl}/trips`,
    truckType: `${resourcesUrl}/truckType`,
    truckTypeFilter: `${resourcesUrl}/trucktype-filter`,
    truckStatusFilter: `${resourcesUrl}/truckStatus-filter`,
    carrierStatusFilter: `${resourcesUrl}/carrierStatus-filter`,
    drivers: `${resourcesUrl}/drivers`,
    materials: `${resourcesUrl}/materials`,
    cities: `${resourcesUrl}/cities`,
    division: `${resourcesUrl}/division`,
    country: `${resourcesUrl}/country`,
    swiftCode: `${resourcesUrl}/swiftCode`,
    addTruck: `${resourcesUrl}/addTruck`,
    addTruckDriver: `${resourcesUrl}/addTruckDriver`,
    editTruck: `${resourcesUrl}/editTruck`,
    indentStatus: `${resourcesUrl}/indent-status`,
    roles: `${resourcesUrl}/roles`,
    companyComment: `${resourcesUrl}/company-comment`,
    truckComment: `${resourcesUrl}/truck-comment`,
    indentComment: `${resourcesUrl}/indent-comment`,
    tripComment: `${resourcesUrl}/trip-comment`,
    tripShowMore: `${resourcesUrl}/trip-showmore`,
    tripCall: `${resourcesUrl}/trip-call`,
    bankBalance: `${resourcesUrl}/bankBalance`,
    searchCompany: `${resourcesUrl}/searchCompany`,
    tripTimeUpdate: `${resourcesUrl}/tripTimeUpdate`,
    tripFiles: `${resourcesUrl}/tripFiles`,
    ctdFiles: `${resourcesUrl}/company-truck-driver`,
    updateCompany: `${resourcesUrl}/updateCompany`,
    creditDebit: `${resourcesUrl}/creditDebit`,
    creditDebitReject: `${resourcesUrl}/creditDebitReject`,
    creditDebitApprove: `${resourcesUrl}/creditDebitApprove`,
    generateInvoice: `${resourcesUrl}/generateInvoice`,
    companyType: `${resourcesUrl}/companyType`,
    chargeType: `${resourcesUrl}/selectChargeType`,
    selectCustomerAddress: `${resourcesUrl}/selectCustomerAddress`,
    editVatNumber: `${resourcesUrl}/editVatNumber`,
    shipperIncoming: `${resourcesUrl}/shipperIncoming`,
    systemMamul: `${resourcesUrl}/systemMamul`,
    truckTypeGroup: `${resourcesUrl}/selecttruckTypeGroup`,
    requesttruckType: `${resourcesUrl}/approvetrucktype`,
    borderChargeNames : `${resourcesUrl}/borderChargeNames`,
    selectLeadChannel: `${resourcesUrl}/selectLeadChannel`,
    selectEmployeeFunction: `${resourcesUrl}/selectEmployeeFunction`,
    selectEmployeeManager: `${resourcesUrl}/selectEmployeeManager`,
    addBranchEmployee: `${resourcesUrl}/addBranchEmployee`,
    selectEmployeeTeam: `${resourcesUrl}/selectEmployeeTeam`,
    employeeTeamList: `${resourcesUrl}/employeeTeamList`, 
    selectCustomerInvoicedTrips: `${resourcesUrl}/selectCustomerInvoicedTrips`,

  },

  screen: {
    REGISTRATION: "REGISTRATION",
    LOGIN: "LOGIN",
    UPDATE_PASSWORD: "UPDATE_PASSWORD",
  },
};
